/**
 * Хуки Preact для стора и языка. Подписка — на выбранный кусок: компонент перерисовывается, только когда результат
 * `select` изменился (по `Object.is` или своей `equal`, например {@link shallowEqual} для объектов).
 */
import { useLayoutEffect, useReducer, useRef } from "preact/hooks";
import { t } from "../i18n";
import { appStore, type Store } from "./store";

export function shallowEqual<T>(a: T, b: T): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  return ka.every((k) => Object.is((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
}

/** Выбранная часть стора; перерисовка — при её изменении. */
export function useStore<T, S>(store: Store<T>, select: (s: T) => S, equal: (a: S, b: S) => boolean = Object.is): S {
  const [, force] = useReducer((n: number) => n + 1, 0);
  const value = select(store.get());
  const latest = useRef({ value, select, equal });
  latest.current = { value, select, equal };
  useLayoutEffect(
    () =>
      store.subscribe((s) => {
        const cur = latest.current;
        if (!cur.equal(cur.select(s), cur.value)) force(0);
      }),
    [store],
  );
  return value;
}

/** `t` с перерисовкой при смене языка. */
export function useT(): typeof t {
  useStore(appStore, (s) => s.locale);
  return t;
}
